import { z } from "zod";
import { SharedApiError, normalizeApiBaseUrl, type FetchLike } from "./shared";

export const listChannelsQuerySchema = z.object({
  workspace_id: z.string().min(1),
});

export const channelItemSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  type: z.string(),
  is_private: z.boolean(),
  is_archived: z.boolean(),
  member_count: z.number().int(),
});

export const listChannelsSuccessSchema = z.object({
  channels: z.array(channelItemSchema),
});

export type ChannelItem = z.infer<typeof channelItemSchema>;
export type ListChannelsSuccess = z.infer<typeof listChannelsSuccessSchema>;

export function listChannelsPath(workspaceId: string): string {
  return `/api/channels?workspace_id=${encodeURIComponent(workspaceId)}`;
}

export async function listChannelsRequest({
  apiBaseUrl,
  accessToken,
  workspaceId,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  workspaceId: string;
  fetchImpl?: FetchLike;
}): Promise<ListChannelsSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${listChannelsPath(workspaceId)}`,
    {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const msg =
      payload && typeof payload === "object" && "error" in payload
        ? (payload as { error: string }).error
        : "Failed to list channels";
    throw new SharedApiError(msg, response.status);
  }

  const payload = await response.json().catch(() => null);
  const parsed = listChannelsSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid list channels response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}
